import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { MainButton } from "../../../components/MainButton";
import { ConfigType } from "../../../contexts/AppConfigContext";
import { useAppConfig } from "../../../hooks/useAppConfig";
import { usePluginspace } from "../../../hooks/usePluginspace";
import { useSeller } from "../../../hooks/useSeller";
import api from "../../../services/api";
import Swal from "sweetalert2";
import { CopyToClipboard } from "react-copy-to-clipboard";
import "./qrcode.css";
import NoImg from "../../../assets/images/no-image.png";
// Ícones
import { ShareIcon } from "../../../components/MenuIcons/ShareIcon";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import Loading from "react-loading";


type Catalog = {
  id: number;
  pluginspaceId: number;
  userId: number;
  catalog: string;
  status: number;
  imgCatalog: string;
  dateStart: string;
  dateEnd: string;
  created: string;
  deleted: string | null;
  order: number;
  updated: string;
  sellerId: number | null;
  linkCatalog: string | null;
  sellerCatalog: boolean;
  isExclusive: boolean;
  products: any[];
  // Campos calculados
  name: string;
  description: string;
  image: string;
  url: string;
  qrCodeUrl: string;
  active: boolean;
};

type SellerData = {
  id: number;
  name: string;
  email: string;
  cpf: string;
  sellerCode: string;
  phone: string
  url: string;
};

export function SellerQRCode() {
  const navigate = useNavigate();
  const { pluginspace_id } = useParams();
  const { getAppConfig } = useAppConfig();
  const { registerAccess,getThisPluginspace } = usePluginspace();
  const { getTokenSellertoRequest, getSellerUser } = useSeller();

  const [loading, setLoading] = useState(true);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [config, setConfig] = useState<ConfigType | void>();
  const [sellerData, setSellerData] = useState<SellerData>({
    id: 0,
    name: "",
    email: "",
    phone: "",
    cpf: "",
    sellerCode: "",
    url: "",
  });
  const [catalogs, setCatalogs] = useState<Catalog[]>([]);

  // Estados para controlar abertura/fechamento dos cards
  const [isMainCardOpen, setIsMainCardOpen] = useState(false);
  const [openCatalogCards, setOpenCatalogCards] = useState<Set<number>>(new Set());

  // URL base para links do vendedor
  const baseUrl = window.location.origin;
  const [sellerUrl, setSellerUrl] = useState(`${baseUrl}/${pluginspace_id}`);
  const [sellerQRCodeUrl, setSellerQRCodeUrl] = useState(`${baseUrl}/${pluginspace_id}`);
  const [text, setText] = useState<string>(`${baseUrl}/${pluginspace_id}`);

  async function getConfig() {
    setConfig(await getAppConfig());
    await getSellerInfo();

    setLoading(false);
  }

  async function getSellerInfo() {
    try {
      await getSellerUser().then(async (user) => {
        if (user) {
          const url = `${baseUrl}/${pluginspace_id}/category?vendor=${window.btoa(user.cpf)}`
          const userData= {
            id: user.id,
            name: user.name,
            email: user.email,
            cpf: user.cpf,
            sellerCode: user.sellerCode || "",
            phone: user.numberPhone || "",
            url: url,
          }
          setSellerData(userData);

          setSellerQRCodeUrl(url);
          setSellerUrl(url);

          await getCatalogs(user.id);
          await defineShareLink(userData,url)
        }
      }).catch((error) => {
        console.error("Erro ao buscar informações do vendedor:", error);
      });
    } catch (error) {
      console.log(error);
    }
  }

  async function getCatalogs(sellerId: number) {
    try {
      setLoadingCatalogs(true);

      await api
        .post("/api/catalog/filter", {
          sellerId,
        }, {
          headers: {
            Authorization: `Bearer ${await getTokenSellertoRequest()}`,
          },
        })
        .then((response: any) => {
          if (response.data && response.data.data) {

            const catalogsData = response.data.data.map((catalog: any) => ({
              ...catalog,
              // Campos calculados para compatibilidade
              name: catalog.catalog,
              description: `${catalog.products?.length || 0} produtos`,
              image: catalog.imgCatalog || NoImg,
              url: `${baseUrl}/${pluginspace_id}/catalog/${window.btoa(catalog.id)}${catalog.linkCatalog}`,
              qrCodeUrl: `${baseUrl}/${pluginspace_id}/catalog/${window.btoa(catalog.id)}${catalog.linkCatalog}`,
              active: catalog.status === 1,
            }));
            setCatalogs(catalogsData);
          }
          setLoadingCatalogs(false);
        })
        .catch((err: any) => {
          setLoadingCatalogs(false);
          console.log("Erro ao buscar catálogos:", err);
          // Dados de exemplo para demonstração
          setCatalogs([]);
        });
    } catch (error) {
      console.log(error);
      setLoadingCatalogs(false);
    }
  }

  const handleCopySuccess = (item: string) => {
    Swal.fire({
      icon: "success",
      title: "Link Copiado!",
      text: `Link ${item} foi copiado para a área de transferência.`,
      timer: 2000,
      showConfirmButton: false,
    });
  };

  const handleWhatsAppShare = (url: string, title: string) => {
    const message = `Confira ${title}: ${url}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  const downloadQRCode = (url: string, filename: string) => {
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(url)}`;
    const link = document.createElement('a');
    link.download = `${filename}.png`;
    link.href = qrUrl;
    link.target = '_blank';
    link.click();
  };

  // Funções para controlar abertura/fechamento dos cards
  const toggleMainCard = () => {
    setIsMainCardOpen(!isMainCardOpen);
  };

  const toggleCatalogCard = (catalogId: number) => {
    const newOpenCards = new Set(openCatalogCards);
    if (newOpenCards.has(catalogId)) {
      newOpenCards.delete(catalogId);
    } else {
      newOpenCards.add(catalogId);
    }
    setOpenCatalogCards(newOpenCards);
  };

  // Função para gerar URL do QR Code
  const generateQRCodeUrl = (data: string, size: number = 150) => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(data)}`;
  };

  async function defineShareLink(userData: any, urlSeller: string) {

    let text = ``;
    const ps = await getThisPluginspace();
    if (ps) {
      text = text.concat(
        `${ps.name}\nEstamos te esperando, corra na nossa loja digital e faça suas compras!\n`
      );
    }
    let url = `\n${urlSeller}\n`;
    text = text.concat(url);
    if (userData && userData.phone) {
      text = text.concat(
        `\nPara mais informações, pode chamar no seguinte número: ${userData.phone}, meu nome é ${userData.name}\ne estarei a disposição para qualquer dúvida`
      );
    }
    setText(text);
  }

  useEffect(() => {
    registerAccess("seller-qrcode");
    getConfig();
  }, []);

  if (loading) {
    return (
      <div style={{ width: "100%", height: "calc(100vh)", display: "flex", justifyContent: "center", alignItems: "center", backgroundColor: "#8d8d8dff" }}>
        <Loading
          type="spinningBubbles"
          color="#FFF"
          width={64}
          height={64}
        />
      </div>
    );
  }

  return (
    <div className="seller-qrcode-page">
      <div className="content-seller-qrcode">
        <div className="title-seller-qrcode">
          <h2>Meus Links e QR Codes</h2>
          <svg
            width="17"
            height="17"
            viewBox="0 0 17 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={() => navigate(-1)}
            style={{ cursor: "pointer" }}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.978551 2.21657L6.957 8.2395L0.978551 14.2624L2.97137 16.2701L8.94981 10.2471L14.9283 16.2701L16.9211 14.2624L10.9426 8.2395L16.9211 2.21657L14.9283 0.208931L8.94981 6.23186L2.97137 0.208931L0.978551 2.21657Z"
              fill="#636363"
            />
          </svg>
        </div>

        <div className="seller-qrcode-body">
          {/* Card do Link Principal do Vendedor */}
          <div className="qr-card main-seller-card">
            <div className="qr-card-header" onClick={toggleMainCard}>
              <div className="qr-card-info">
                <h3>Meu Link de Vendas</h3>
                <p>Link principal para seus clientes</p>
              </div>
              <div className="toggle-icon">
                <span style={{ fontSize: '20px', color: 'white', cursor: 'pointer' }}>
                  {isMainCardOpen ? <FaChevronDown color="#155724" /> : <FaChevronRight color="#155724" />}
                </span>
              </div>
            </div>

            {isMainCardOpen && (
              <>
                <div className="qr-code-container">
                  <img
                    src={generateQRCodeUrl(sellerData.url, 150)}
                    alt="QR Code do Vendedor"
                    className="qr-code"
                  />
                </div>

                <div className="action-buttons">
                  <CopyToClipboard
                    text={text}
                    onCopy={() => handleCopySuccess("do vendedor")}
                  >
                    <MainButton
                      color="#fff"
                      backgroundColor="#007bff"
                      text="Copiar Link"
                    />
                  </CopyToClipboard>

                  <MainButton
                    color={
                      config?.webappcolor.fontbutton
                        ? config.webappcolor.fontbutton
                        : "#636363"
                    }
                    backgroundColor={
                      config?.webappcolor.webappbutton
                        ? config.webappcolor.webappbutton
                        : "#EAEAEA"
                    }
                    text="Baixar QR"
                    onClick={() => downloadQRCode(sellerQRCodeUrl, `loja-${sellerData.name}`)}
                  />
                </div>
              </>
            )}
          </div>

          {/* Seção de Catálogos */}
          <div className="catalogs-section">
            <div className="section-header">
              <h3>Meus Catálogos</h3>
              <p>Compartilhe catálogos específicos com seus clientes</p>
            </div>

            {loadingCatalogs ? (
              <div className="loading-catalogs">
                <Loading
                  type="bubbles"
                  color="#007bff"
                  width={50}
                  height={50}
                />
                <p>Carregando catálogos...</p>
              </div>
            ) : catalogs.length > 0 ? (
              <div className="catalogs-list">
                {catalogs.map((catalog) => (
                  <div
                    key={catalog.id}
                    className={`qr-card catalog-card${openCatalogCards.has(catalog.id) ? " active" : ""}`}
                  >
                    <div
                      className="qr-card-header"
                      onClick={() => toggleCatalogCard(catalog.id)}
                      style={{

                      }}
                    >
                      <div className="catalog-image">
                        <img
                          src={catalog.image}
                          alt={catalog.name}
                        />
                      </div>
                      <div className="qr-card-info">
                        <h4>{catalog.name}</h4>
                        <p>{catalog.description}</p>
                        <span className={`status ${catalog.active ? 'active' : 'inactive'}`}>
                          {catalog.active ? 'Ativo' : 'Inativo'}
                        </span>
                      </div>
                      <div className="toggle-icon">
                        <span style={{ fontSize: '16px', color: '#636363', cursor: 'pointer' }}>
                          {openCatalogCards.has(catalog.id) ? <FaChevronDown color="#636363" /> : <FaChevronRight color="#636363" />}
                        </span>
                      </div>
                    </div>

                    {openCatalogCards.has(catalog.id) && (
                      <>
                        <hr style={{
                          backgroundColor: '#d4edda',
                          border: 'none',
                          height: '1px',
                          margin: '8px 0'
                        }} />
                        <div className="qr-code-container small">
                          <img
                            src={generateQRCodeUrl(catalog.qrCodeUrl, 100)}
                            alt={`QR Code do ${catalog.name}`}
                            className="qr-code small"
                          />
                        </div>

                        <div className="action-buttons small">
                          <CopyToClipboard
                            text={catalog.url}
                            onCopy={() => handleCopySuccess(`do catálogo "${catalog.name}"`)}
                          >
                            <button className="action-btn copy-btn">
                              Copiar
                            </button>
                          </CopyToClipboard>

                          <button
                            className="action-btn download-btn"
                            onClick={() => downloadQRCode(catalog.qrCodeUrl, `catalogo-${catalog.name}`)}
                          >
                            Baixar QR
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-catalogs">
                <div className="no-catalogs-icon">📱</div>
                <h4>Nenhum catálogo encontrado</h4>
                <p>Você ainda não possui catálogos criados.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
